import React, { useState } from 'react';
import { Mail, Link as LinkIcon, Calendar, Hash, Copy, Check, ExternalLink } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface FieldValue {
  label: string;
  path: string;
  value: string | number | boolean | null | undefined;
}

interface ToolResultFieldProps {
  field: FieldValue;
  copyable?: boolean;
  onLinkClick?: (href: string) => void;
}

type FieldType = 'email' | 'url' | 'id' | 'date' | 'number' | 'boolean' | 'text';

/**
 * Smart field renderer that detects field type and renders appropriately
 */
export const ToolResultField: React.FC<ToolResultFieldProps> = ({
  field,
  copyable = true,
  onLinkClick
}) => {
  const [copied, setCopied] = useState(false);
  const { label, path, value } = field;

  // Handle null/undefined values
  if (value === null || value === undefined) {
    return (
      <div className="py-2">
        <div className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
          {label}
        </div>
        <div className="text-sm text-gray-400 italic">Not set</div>
      </div>
    );
  }

  // Detect field type
  const detectFieldType = (): FieldType => {
    const labelLower = label.toLowerCase();
    const stringValue = String(value);

    // Email detection
    if (labelLower.includes('email') || /^[\w-\.]+@([\w-]+\.)+[\w-]{2,4}$/.test(stringValue)) {
      return 'email';
    }

    // URL detection
    if (labelLower.includes('link') || labelLower.includes('url') ||
        path.toLowerCase().includes('url') ||
        /^https?:\/\//.test(stringValue)) {
      return 'url';
    }

    // ID detection
    if (labelLower.includes('id') || path.includes('.id') || path.endsWith('Id')) {
      return 'id';
    }

    // Date detection
    if (labelLower.includes('date') || labelLower.includes('time') ||
        path.includes('date') || path.includes('created') || path.includes('updated') ||
        /^\d{4}-\d{2}-\d{2}/.test(stringValue)) {
      return 'date';
    }

    // Boolean detection
    if (typeof value === 'boolean') {
      return 'boolean';
    }

    // Number detection
    if (typeof value === 'number') {
      return 'number';
    }

    return 'text';
  };

  const fieldType = detectFieldType();

  // Copy to clipboard handler
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(String(value));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  // Format date value
  const formatDate = (dateStr: string): string => {
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateStr;
    }
  };

  // Get field icon
  const getFieldIcon = () => {
    const iconClass = "w-3 h-3";
    switch (fieldType) {
      case 'email':
        return <Mail className={iconClass} />;
      case 'url':
        return <LinkIcon className={iconClass} />;
      case 'id':
        return <Hash className={iconClass} />;
      case 'date':
        return <Calendar className={iconClass} />;
      default:
        return null;
    }
  };

  // Render field value based on type
  const renderValue = () => {
    const stringValue = String(value);

    switch (fieldType) {
      case 'email':
        return (
          <a
            href={`mailto:${stringValue}`}
            className="text-blue-600 hover:text-blue-800 hover:underline flex items-center gap-1 group"
            onClick={(e) => {
              if (onLinkClick) {
                e.preventDefault();
                onLinkClick(`mailto:${stringValue}`);
              }
            }}
          >
            <span>{stringValue}</span>
            <ExternalLink className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
          </a>
        );

      case 'url':
        return (
          <a
            href={stringValue}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 hover:text-blue-800 hover:underline flex items-center gap-1 group"
            onClick={(e) => {
              if (onLinkClick) {
                e.preventDefault();
                onLinkClick(stringValue);
              }
            }}
          >
            <span className="truncate">{stringValue}</span>
            <ExternalLink className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
          </a>
        );

      case 'date':
        return (
          <span className="text-sm text-gray-700">
            {formatDate(stringValue)}
          </span>
        );

      case 'boolean':
        return (
          <span className={cn(
            "inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium",
            value === true ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-800"
          )}>
            {value === true ? 'Yes' : 'No'}
          </span>
        );

      case 'number':
        return (
          <span className="text-sm font-medium text-gray-900">
            {typeof value === 'number' ? value.toLocaleString() : value}
          </span>
        );

      case 'id':
        return (
          <code className="text-xs bg-gray-100 px-2 py-0.5 rounded font-mono text-gray-700">
            {stringValue}
          </code>
        );

      default:
        return (
          <span className="text-sm text-gray-900">
            {stringValue}
          </span>
        );
    }
  };

  return (
    <div className="py-2 group">
      <div className="flex items-center gap-1.5 text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
        {getFieldIcon()}
        <span>{label}</span>
      </div>
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          {renderValue()}
        </div>
        {copyable && !['boolean'].includes(fieldType) && (
          <button
            onClick={handleCopy}
            className="flex-shrink-0 p-1 text-gray-400 hover:text-gray-600 opacity-0 group-hover:opacity-100 transition-opacity rounded hover:bg-gray-100"
            title="Copy to clipboard"
          >
            {copied ? (
              <Check className="w-3.5 h-3.5 text-green-600" />
            ) : (
              <Copy className="w-3.5 h-3.5" />
            )}
          </button>
        )}
      </div>
    </div>
  );
};
